// @flow
import React, { useCallback, useMemo, useState } from 'react';
import { map, get } from 'lodash';
import { useSelector } from 'react-redux';
import { SelectBox, Toggle, Input, Button, Modal, Icon } from '@elements';
import { redirectToDashboard } from '@context/user';
import { periodsOptions, months, years } from '@helpers/constants';
import type { DoctorsState, FormElementData } from '@types';
import { sort, alphabeticCompare, useTranslationNs, useTranslatedConstant } from '@helpers/helpers';
import classnames from 'classnames';
import './ReportsFilters.scss';

type Props = {
  doctors: DoctorsState,
  isManager: boolean,
  isLoading: boolean,
  onChangeFilter: Function,
  withNotaFiscal?: boolean,
  status?: string,
  notaFiscal?: boolean,
  doctor: string,
  isMobile?: boolean,
  year?: number;
  month: number;
  reportsSelectDoctor: boolean,
  onCloseMobileModal?: Function,
  period?: string;
  reportType: string,
};


const ReportsFilters = ({
  doctors, isLoading, doctor, status, withNotaFiscal, isManager, period, reportsSelectDoctor,
  isMobile, onChangeFilter, onCloseMobileModal, reportType, ...props
} : Props) => {
  const [_] = useTranslationNs('reports');

  const year = useMemo(() => String(props.year), [props.year]);
  const month = useMemo(() => String(props.month), [props.month]);

  const notaFiscal = useMemo(() => Boolean(props.notaFiscal), [props.notaFiscal]);

  const filterStatusOptions = useMemo(() => [
    { text: _('filters/status/confirmed'), value: 'confirmed' },
    { text: _('filters/status/forecasted'), value: 'forecasted' },
    { text: _('filters/status/canceled'), value: 'canceled' },
    { text: _('filters/status/health_insurance'), value: 'health_insurance' },
  ], [_]);

  const [translatedPeriodOptions] = useTranslatedConstant(periodsOptions);
  const [translatedMonths] = useTranslatedConstant(months);

  const doctorsData = useMemo(() => get(doctors, ['data'], {}), [doctors]);

  const doctorItens = useMemo(() => map<any, any>(doctorsData, (doc) => ({
    value: doc.id,
    text: doc.nameWithTitle,
    name: doc.name,
  })), [doctorsData]);

  const doctorsItems = useMemo(() => [
    { text: _('action/all_doctors', 'global'), value: 'all' },
    ...sort(doctorItens, alphabeticCompare('name')),
  ], [doctorItens, _]);

  const userId = useSelector((state) => get(state, ['context', 'user', 'id']));
  const clinicId = useSelector((state) => get(state, ['context', 'clinics', 'activeClinic', 'id']));
  const accessToken = useSelector((state) => get(state, ['auth', 'token']));
  const [periodSelectKey, setPeriodSelectKey] = useState(0);

  const handleChangeFilter = useCallback((data: FormElementData) => {
    if (data.name === 'period' && data.value === 'custom_date') {
      // 'custom_date' is not a real period: it deep-links to the dashboard reports,
      // which support free start/end dates. Remount the SelectBox so it snaps back
      // to the active period instead of displaying the swallowed selection.
      redirectToDashboard(`/users/${userId}/clinics/${clinicId}/reports/${reportType}`, accessToken);
      setPeriodSelectKey((key) => key + 1);
      return;
    }
    onChangeFilter(data);
  }, [onChangeFilter, userId, clinicId, accessToken, reportType]);

  const handleCloseMobileModal = useCallback(() => {
    if (onCloseMobileModal) {
      onCloseMobileModal();
    }
  }, [onCloseMobileModal]);

  const content = (mobile: boolean) => (
    <div className={classnames('reportsFilter', { mobile })}>
      <SelectBox
        key={periodSelectKey}
        disabled={isLoading}
        className="input-periods"
        label={_('filters/label_period')}
        placeholder={_('label/today', 'global')}
        name="period"
        onChange={handleChangeFilter}
        value={period}
        options={translatedPeriodOptions}
        changeablePlaceholder
      />
      {period === 'custom_month' &&
      <SelectBox
        name="month"
        disabled={isLoading}
        className="filter-month"
        label={_('label/month', 'global')}
        options={translatedMonths}
        value={month}
        onChange={handleChangeFilter}
        changeablePlaceholder
      />
        }
      {(period === 'custom_month' || period === 'custom_year') &&
      <SelectBox
        name="year"
        disabled={isLoading}
        className="filter-year"
        label={_('label/year', 'global')}
        options={years}
        value={year}
        onChange={handleChangeFilter}
        changeablePlaceholder
      />
        }
      {!isManager &&
      <div className="inputHolder filter-type">
        <label className="formInputLabel">{_('label/doctor', 'global')}</label>
        <Input disabled value={get(doctorsData, [doctor, 'nameWithTitle'])} />
      </div>
        }
      {isManager && reportsSelectDoctor &&
      <SelectBox
        disabled={isLoading}
        name="doctor"
        className="input-doctors"
        label={_('label/doctor', 'global')}
        options={doctorsItems}
        value={doctor}
        onChange={handleChangeFilter}
        changeablePlaceholder
      />
        }
      {status &&
      <SelectBox
        name="status"
        disabled={isLoading}
        className="filter-type"
        label={_('filters/status')}
        options={filterStatusOptions}
        value={status}
        onChange={onChangeFilter}
        changeablePlaceholder
      />
        }
      {withNotaFiscal &&
      <Toggle
        name="notaFiscal"
        label={_('filters/fiscal_note_only')}
        onChange={onChangeFilter}
        isChecked={notaFiscal}
      />
        }
    </div>
  );

  if (isMobile) {
    return (
      <Modal className="asView">
        <div className="modalHeader">
          {/* The filters apply as they change: a back arrow, like the agenda's and the patients' filters */}
          <Button onClick={handleCloseMobileModal}>
            <Icon iconName="arrow-left" className="icon" />
          </Button>
          <span className="title">{_('filters/title')}</span>
          <Button className="primary" text={_('action/filter', 'global')} onClick={handleCloseMobileModal} />
        </div>
        <div className="modalGrid">
          <div className="modalContainer">
            <div className="modalContent">
              {content(true)}
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  return content(false);
};


export default ReportsFilters;
